<template>
  <div class="border_8 p-4 rounded-lg">
    <div class="flex flex-row justify-between items-center">
      <label class="text-[15px] text_color font-semibold">{{
        actionName
      }}</label>
      <a-select
        :value="modelValue || POLICY_SCOPES.NONE"
        :show-arrow="true"
        :disabled="disabled"
        option-label-prop="label"
        @change="handleScopeChange"
        class="crm_view_select w-[250px]"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4"
          />
        </template>

        <a-select-option
          v-for="option in scopeOptions"
          :key="option.value"
          :value="option.value"
          :label="option.label"
        >
          <div class="flex gap-2 items-center">
            <ThemeImage
              :icon="imageNames.TICK_GREY"
              alt="check_icon"
              :class="`${modelValue !== option.value && 'invisible'}`"
            />
            <span class="proxima_nova text_color">{{ option.label }}</span>
          </div>
        </a-select-option>
      </a-select>
    </div>

    <a-button
      v-if="modelValue === POLICY_SCOPES.CONDITIONAL && !showCondition"
      class="secondary_medium mt-1"
      @click="handleAddCondition"
    >
      <template #icon>
        <ThemeImage :icon="imageNames.PLUS" alt="plus_icon" class="size-4" />
      </template>
      <span class="font-semibold"> Add condition</span>
    </a-button>

    <ConditionalAccessSection
      v-if="modelValue === POLICY_SCOPES.CONDITIONAL && showCondition"
      :action-key="actionKey"
      :condition-type="conditionData?.type"
      :assigned-property="conditionData?.property"
      :specific-users="conditionData?.users || []"
      :specific-teams="conditionData?.teams || []"
      :object-properties="objectProperties"
      :users-teams-options="usersTeamsOptions"
      :disabled="disabled"
      @update="handleConditionUpdate"
      @remove="handleRemoveCondition"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { POLICY_SCOPES } from "@/src/constants/permissionConstants.js";
import ConditionalAccessSection from "./ConditionalAccessSection.vue";

const props = defineProps({
  actionName: { type: String, required: true },
  actionKey: { type: String, required: true },
  modelValue: { type: String, default: POLICY_SCOPES.NONE },
  scopeOptions: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
  permissionKey: { type: String, required: true },
  objectProperties: { type: Array, default: () => [] },
  conditionData: { type: Object, default: () => null },
  usersTeamsOptions: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:modelValue", "condition-update"]);

const showCondition = ref(false);

const handleScopeChange = (value) => {
  emit("update:modelValue", value);
};

const handleAddCondition = () => {
  showCondition.value = true;
  if (!props.conditionData?.type) {
    emit("condition-update", {
      actionKey: props.actionKey,
      key: "type",
      value: "user_in_role",
    });
  }
};

const handleRemoveCondition = () => {
  showCondition.value = false;
  emit("condition-update", {
    actionKey: props.actionKey,
    key: "type",
    value: null,
  });
  emit("condition-update", {
    actionKey: props.actionKey,
    key: "property",
    value: null,
  });
  emit("condition-update", {
    actionKey: props.actionKey,
    key: "users",
    value: [],
  });
  emit("condition-update", {
    actionKey: props.actionKey,
    key: "teams",
    value: [],
  });
};

const handleConditionUpdate = ({ key, value }) => {
  emit("condition-update", { actionKey: props.actionKey, key, value });
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== POLICY_SCOPES.CONDITIONAL) {
      showCondition.value = false;
    }
  }
);

onMounted(() => {
  if (props.conditionData?.type) {
    showCondition.value = true;
  }
});
</script>

<style scoped>
.crm_view_select :deep(.ant-select-dropdown) {
  width: auto !important;
}
</style>
